import * as _ from 'lodash';
import * as React from 'react';
import { Container, FormFeedback, FormGroup, Input, Label, Modal, ModalBody } from 'reactstrap';

import Localization from '../../config/localization';

import { DATE_WARNING } from '../../constants/backOffice';
import { SECTOR_ID_GAS } from '../../constants/sectorId';

import * as AidsApi from '../../api/aids';
import * as PartnersApi from '../../api/partners';
import * as UtilApi from '../../api/util';

import Notifications from '../../lib/notifications';
import {
  DateStringToMaxValue,
  DateStringToMinValue,
  DateStringToTimeStamp,
  DownloadFile,
  GetFirstMonthAsDate,
  GetLastMonthAsDate,
  ToFloat,
  ToNumber,
  ToString
} from '../../lib/utils';
import { ConsumptionPointValidation } from '../../lib/validator';

import BackButton from '../../components/BackButton';
import Client from '../../components/Client';
import Collapsible from '../../components/Collapsible';
import Computation from '../../components/Computation';
import Loader from '../../components/Loader';
import DownloadSvg from '../../components/Svg/download';

import './BackOfficeComputation.scss';
import { FILE_FORMATS } from '../../constants/file';

class BackOfficeComputation extends React.Component<BackOfficeComputation.IProps, BackOfficeComputation.IState> {
  state: BackOfficeComputation.IState = {
    partner: undefined,

    MONTHS: undefined,

    formWasSubmitted: false,
    stopPropagation: false,

    dateWarning: false,

    consumptionPointIntervalId: undefined,
    endDate: '',
    price: 0,
    showModal: false,
    startDate: '',

    annexEndDate: '',
    annexStartDate: '',

    maxAnnexEndDate: '',
    minAnnexStartDate: '',

    consumptionPointsInterval: [],

    loading: false,
    loadingMessage: ''
  };

  async componentDidMount() {
    await Promise.all([this._getPartner(), this._getMonths(), this._getConsumptionPointInterval()]);

    return this.setState({ startDate: ToString(this._getMinValue()) });
  }

  renderTrigger = (consumptionPointInterval: Aid.IAidConsumptionPointInterval) => {
    return (
      <React.Fragment>
        <span className="my-auto h-100">
          {Localization.formatString(
            Localization.backOfficeComputationComputationAnnexPeriod,
            ToString(consumptionPointInterval.startDate),
            ToString(consumptionPointInterval.endDate)
          )}
        </span>
        <span className="my-auto h-100">
          {Localization.formatString(
            Localization.backOfficeComputationComputationAnnexConsumption,
            ToString(consumptionPointInterval.consum, '0')
          )}
        </span>
        <span className="my-auto h-100">
          {Localization.formatString(
            Localization.backOfficeComputationComputationAnnexAmount,
            ToString(consumptionPointInterval.consumEfectiv, '0')
          )}
        </span>

        <a className="my-auto h-100 d-flex link-color" onMouseOver={this._onMouseOver} onMouseOut={this._onMouseOut}>
          <span className="px-1 text-nowrap">{Localization.backOfficeComputationComputationAnnexDownload}</span>
          <DownloadSvg className="m-auto" />
        </a>
      </React.Fragment>
    );
  };

  renderAnnexTable = () => {
    if (!this.state.consumptionPointsInterval || !this.state.consumptionPointsInterval.length) {
      return null;
    }

    this._sortConsumption(this.state.consumptionPointsInterval);

    return (
      <React.Fragment>
        <h6 className="mt-5 eon-title eon-red text-nowrap custom-font-size mb-2 strong">
          {Localization.backOfficeComputationComputationAnnex}
        </h6>

        {this.state.consumptionPointsInterval.map((consumptionPointInterval, index) => (
          <Collapsible
            onClickCapture={event => this._onClickCapture(event, consumptionPointInterval)}
            key={consumptionPointInterval.consumptionPointIntervalId}
            trigger={this.renderTrigger(consumptionPointInterval)}
            className="mt-1"
            onOpen={() =>
              !(consumptionPointInterval.aids && consumptionPointInterval.aids.length) &&
              this._getAids(
                {
                  consumptionPointIntervalId: consumptionPointInterval.consumptionPointIntervalId,
                  endDate: consumptionPointInterval.endDate,
                  startDate: consumptionPointInterval.startDate
                },
                consumptionPointInterval
              )
            }
            onClose={() => this._closeEditExistingInterval(consumptionPointInterval, index)}
          >
            <Computation
              api={AidsApi.AidCalculate}
              isEdit
              editData={consumptionPointInterval.editData}
              min={this._getMinValue(
                (this.state.consumptionPointsInterval && this.state.consumptionPointsInterval[index - 1]) || consumptionPointInterval,
                !index
              )}
              max={
                this.state.consumptionPointsInterval && this.state.consumptionPointsInterval[index + 1]
                  ? DateStringToMaxValue(this.state.consumptionPointsInterval[index + 1].startDate)
                  : GetLastMonthAsDate(this.state.MONTHS)
              }
              endDate={ToString(consumptionPointInterval.endDate)}
              startDate={ToString(consumptionPointInterval.startDate)}
              price={ToFloat(consumptionPointInterval.price)}
              aids={consumptionPointInterval.aids}
              SetValue={event => this._setValue(event, consumptionPointInterval)}
              SetEdit={() => this._editExistingInterval(consumptionPointInterval)}
              CreateInterval={() => this._editInterval(consumptionPointInterval, index)}
              SetConsumption={event => this._setConsumption(event, consumptionPointInterval.aids)}
              OnClose={() => this._closeEditExistingInterval(consumptionPointInterval, index)}
            />
          </Collapsible>
        ))}
      </React.Fragment>
    );
  };

  renderAddPeriod = () => {
    if (!this.state.MONTHS || DateStringToTimeStamp(this.state.startDate) > DateStringToTimeStamp(GetLastMonthAsDate(this.state.MONTHS))) {
      return null;
    }

    return (
      <React.Fragment>
        <h5
          className={`eon-title eon-red text-nowrap custom-font-size strong mt-5 ${
            this.state.consumptionPointsInterval && this.state.consumptionPointsInterval.length ? 'pt-5' : ''
          }`}
        >
          {Localization.backOfficeComputationAddPeriod}
        </h5>
        <h6 className="mt-2 eon-title eon-middle-grey">{Localization.backOfficeComputationAddPeriodTips}</h6>

        <Computation
          api={AidsApi.AidCalculate}
          min={this._getMinValue()}
          max={GetLastMonthAsDate(this.state.MONTHS)}
          endDate={this.state.endDate}
          startDate={this.state.startDate}
          price={this.state.price}
          aids={this.state.aids}
          SetValue={this._setValue}
          CreateInterval={() => this._createInterval()}
          SetConsumption={this._setConsumption}
        />
      </React.Fragment>
    );
  };

  render() {
    return (
      <Container className="back-office-computation px-5 py-4 my-4 position-relative">
        <BackButton />

        <Loader loading={this.state.loading} loadingMessage={this.state.loadingMessage} />

        <div className="mt-3 d-flex justify-content-between">
          <div>
            <h4 className="mt-2 eon-title eon-red text-nowrap">{Localization.backOfficeComputationTitle}</h4>
            <h6 className="mt-2 eon-title eon-middle-grey">{Localization.backOfficeComputationTips}</h6>
          </div>
          {this.state.partner && (
            <Client
              hideTitle
              partner={this.state.partner}
              isNaturalGas={this._isNaturalGas()}
              showUtilityType={true}
              extraClassName="client-card"
            />
          )}
        </div>

        {this.renderAnnexTable()}
        {this.renderAddPeriod()}

        <Modal isOpen={this.state.dateWarning} centered toggle={this._onCloseDateModal}>
          <ModalBody className="m-3">
            <h4 className="eon-title">
              {Localization.formatString(
                Localization.backOfficeComputationModalWarningTitle,
                ToString(
                  this.state.index !== undefined && this.state.consumptionPointsInterval
                    ? this.state.consumptionPointsInterval[this.state.index].startDate
                    : this.state.startDate
                ),
                ToString(
                  this.state.index && this.state.consumptionPointsInterval
                    ? this._getMinValue(this.state.consumptionPointsInterval[this.state.index - 1])
                    : (this.state.consumptionPointsInterval &&
                      this._getMinValue(this.state.consumptionPointsInterval[this.state.consumptionPointsInterval.length - 1])) ||
                    undefined
                )
              )}
            </h4>

            <button className="eon-button bg-eon-red eon-white ml-4 mr-5 mt-4" onClick={this._modalOkPress}>
              <span className="px-3">{Localization.backOfficeComputationModalWarningButtonContinue}</span>
            </button>
            <button className="eon-button bg-eon-lightgrey eon-middle-grey mx-4" onClick={this._onCloseDateModal}>
              <span className="px-3">{Localization.backOfficeComputationModalWarningButtonCancel}</span>
            </button>
          </ModalBody>
        </Modal>

        <Modal isOpen={this.state.showModal} centered toggle={this._onCloseModal}>
          <ModalBody className="m-3">
            <h4 className="mt-2 eon-title eon-red text-nowrap">{Localization.backOfficeComputationModalTitle}</h4>
            <h6 className="my-2 eon-title eon-middle-grey">{Localization.backOfficeComputationModalTips}</h6>

            <FormGroup className="remove-arrow">
              <Label className="text-nowrap" for="annexStartDate">
                {Localization.backOfficeComputationAddFrom}
              </Label>
              <Input
                type="date"
                name="annexStartDate"
                id="annexStartDate"
                min={this.state.minAnnexStartDate}
                max={this.state.maxAnnexEndDate}
                value={this.state.annexStartDate}
                onChange={this._setValue}
                className={`eon-input-text position-relative calendar-field ${
                  !this.state.annexStartDate && this.state.formWasSubmitted ? 'is-field-error' : ''
                } `}
                invalid={!this.state.annexStartDate && this.state.formWasSubmitted}
              />
              <FormFeedback tooltip>{Localization.backOfficeComputationAddFromError}</FormFeedback>
            </FormGroup>

            <FormGroup className="remove-arrow">
              <Label className="text-nowrap" for="annexEndDate">
                {Localization.backOfficeComputationAddTo}
              </Label>
              <Input
                type="date"
                name="annexEndDate"
                id="annexEndDate"
                min={this.state.minAnnexStartDate}
                max={this.state.maxAnnexEndDate}
                value={this.state.annexEndDate}
                onChange={this._setValue}
                className={`eon-input-text position-relative calendar-field ${
                  !this.state.annexEndDate && this.state.formWasSubmitted ? 'is-field-error' : ''
                } `}
                invalid={!this.state.annexEndDate && this.state.formWasSubmitted}
              />
              <FormFeedback tooltip>{Localization.backOfficeComputationAddToError}</FormFeedback>
            </FormGroup>

            <div className="eon-modal-footer">
              {FILE_FORMATS.map(fileFormat => <button key={fileFormat}
                                                      className="eon-button bg-eon-red eon-white ml-4 mr-4"
                                                      onClick={this._downloadAnnex(fileFormat)}>
                <span className="px-3">{Localization.backOfficeComputationModalButtonGenerate} {fileFormat}</span>
              </button>)}

              <button className="eon-button bg-eon-lightgrey eon-middle-grey mx-4 mt-2" onClick={this._onCloseModal}>
                <span className="px-3">{Localization.backOfficeComputationModalButtonCancel}</span>
              </button>
            </div>
          </ModalBody>
        </Modal>
      </Container>
    );
  }

  _getMonths = async () => {
    const response = await UtilApi.GetMonths();

    return this.setState({ MONTHS: response.data || [] });
  };

  _getAids = async (
    data = {
      consumptionPointIntervalId: this.state.consumptionPointIntervalId,
      endDate: this.state.endDate,
      startDate: this.state.startDate
    },
    consumptionPointInterval?: Aid.IAidConsumptionPointInterval
  ) => {
    try {
      if (!data.startDate || !data.endDate) {
        return undefined;
      }

      await this.setState({ loading: true });

      const response = await AidsApi.GetAidForBackOffice({
        consumptionPoint: this.props.match.params.consumptionPoint,
        consumptionPointIntervalId: data.consumptionPointIntervalId,
        endDate: data.endDate,
        startDate: data.startDate
      });

      if (consumptionPointInterval) {
        consumptionPointInterval.aids = response.data || undefined;

        await this.setState({ loading: false });

        return this.forceUpdate();
      }

      return this.setState({
        aids: response.data ? response.data.map(aid => ({ ...aid, consumption: '' })) : undefined,
        loading: false
      });
    } catch (error) {
      if (error.code !== '1015') {
        Notifications.error(error && error.message);
      }

      return this.setState({ loading: false, aids: undefined });
    }
  };

  _getConsumptionPointInterval = async (consumptionPointIntervalId?: number) => {
    try {
      await this.setState({
        loading: true,
        consumptionPointIntervalId: undefined,
        price: 0,
        startDate: '',
        endDate: undefined
      });

      const response = await AidsApi.GetConsumptionPointInterval(this.props.match.params.consumptionPoint);

      const consumptionPointsInterval = response.data || undefined;

      this._sortConsumption(consumptionPointsInterval);

      const foundConsumptionPointInterval =
        consumptionPointIntervalId &&
        consumptionPointsInterval.find(
          consumptionPointInterval => consumptionPointInterval.consumptionPointIntervalId === consumptionPointIntervalId
        );

      if (foundConsumptionPointInterval) {
        await this._getAids(
          {
            consumptionPointIntervalId: foundConsumptionPointInterval.consumptionPointIntervalId,
            endDate: foundConsumptionPointInterval.endDate,
            startDate: foundConsumptionPointInterval.startDate
          },
          foundConsumptionPointInterval
        );
      }

      const lastInterval = this._getLastInterval([...consumptionPointsInterval]);

      await this.setState({
        aids: undefined,
        consumptionPointsInterval,
        loading: false,
        price: ToFloat(lastInterval && lastInterval.price),
        startDate: lastInterval ? DateStringToMinValue(lastInterval.endDate) : ''
      });

      if (!foundConsumptionPointInterval) {
        return undefined;
      }

      return this._getAids();
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _getPartner = async () => {
    try {
      await this.setState({ loading: true });

      const response = await PartnersApi.GetPartner({ search: this.props.match.params.consumptionPoint });

      return this.setState({ partner: (response.data && response.data[0]) || {}, loading: false });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _validation = (
    consumptionPointInterval = {
      consumptionPoint: this.props.match.params.consumptionPoint,
      consumptionPointIntervalId: undefined as number | undefined,
      endDate: this.state.endDate,
      price: this.state.price as string | number,
      startDate: this.state.startDate
    } as Aid.IAidConsumptionPointInterval,
    isEdit = false,
    index = 0
  ) => {
    if (!consumptionPointInterval.startDate) {
      throw new Error(ToString(Localization.formatString(Localization.backOfficeComputationAddFromError, ToString(this._getMinValue()))));
    } else if (!consumptionPointInterval.endDate) {
      throw new Error(
        ToString(Localization.formatString(Localization.backOfficeComputationAddToError, ToString(GetLastMonthAsDate(this.state.MONTHS))))
      );
    } else if (!consumptionPointInterval.price) {
      throw new Error(ToString(Localization.backOfficeComputationAddPriceError));
    } else if (!ConsumptionPointValidation(consumptionPointInterval.consumptionPoint)) {
      throw new Error(ToString(Localization.backOfficeComputationAddConsumptionPointError));
    } else if (isEdit && !consumptionPointInterval.consumptionPointIntervalId) {
      throw new Error(ToString(Localization.backOfficeComputationAddConsumptionPointIntervalError));
    } else if (
      !this.state.dateWarning &&
      DateStringToTimeStamp(consumptionPointInterval.startDate) >
      DateStringToTimeStamp(
        this._getMinValue(isEdit ? this.state.consumptionPointsInterval && this.state.consumptionPointsInterval[index - 1] : undefined)
      )
    ) {
      throw new Error(DATE_WARNING);
    }
  };

  _createInterval = async (consumptionPointIntervalId = this.state.consumptionPointIntervalId) => {
    try {
      await this.setState({ loading: true, formWasSubmitted: true });

      if (consumptionPointIntervalId) {
        return this._saveAids(consumptionPointIntervalId);
      }

      this._validation();

      const response = await AidsApi.CreateInterval({
        consumptionPoint: this.props.match.params.consumptionPoint,
        endDate: this.state.endDate,
        price: ToString(this.state.price),
        startDate: this.state.startDate
      } as Aid.ICreateAidConsumptionPointInterval);

      await this.setState({
        consumptionPointIntervalId: ToNumber(response.data.consumptionPointIntervalId),
        dateWarning: false
      });

      return this._saveAids();
    } catch (error) {
      if (error && error.message === DATE_WARNING) {
        return this.setState({ loading: false, dateWarning: true });
      }

      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _editInterval = async (consumptionPointInterval = {} as Aid.IAidConsumptionPointInterval, index = 0) => {
    try {
      await this.setState({ loading: true, formWasSubmitted: true });

      const data = {
        consumptionPoint: ToString(consumptionPointInterval.consumptionPoint),
        consumptionPointIntervalId: ToNumber(consumptionPointInterval.consumptionPointIntervalId),
        endDate: ToString(consumptionPointInterval.endDate),
        price: ToString(consumptionPointInterval.price),
        startDate: ToString(consumptionPointInterval.startDate)
      } as Aid.IAidConsumptionPointInterval;

      this._validation(data, true, index);

      await AidsApi.EditInterval(data);

      await this.setState({ dateWarning: false });

      return this._saveAids(consumptionPointInterval.consumptionPointIntervalId, consumptionPointInterval.aids);
    } catch (error) {
      if (error && error.message === DATE_WARNING) {
        return this.setState({ loading: false, dateWarning: true, index });
      }

      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _saveAids = async (consumptionPointIntervalId = this.state.consumptionPointIntervalId, aids = this.state.aids) => {
    try {
      await this.setState({ loading: true, formWasSubmitted: false });

      if (!consumptionPointIntervalId) {
        throw new Error();
      }

      const data =
        aids &&
        aids.map<Aid.ISaveAidConsumptionPointInterval>(aid => ({
          consumption: ToFloat(aid.consumption),
          consumptionPointInterval: consumptionPointIntervalId,
          personalIdentificationNumber: ToString(aid.personalIdentificationNumber)
        }));

      if (!data || !data.length) {
        return undefined;
      }

      const isModify = consumptionPointIntervalId !== this.state.consumptionPointIntervalId;

      if (isModify) {
        await AidsApi.ModifyAidsInterval(this.props.match.params.consumptionPoint, data);
      } else {
        await AidsApi.SaveAidsInterval(this.props.match.params.consumptionPoint, data);
      }

      return this._getConsumptionPointInterval(consumptionPointIntervalId);
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _editExistingInterval = (consumptionPointInterval: Aid.IAidConsumptionPointInterval) => {
    consumptionPointInterval.oldData = _.cloneDeep(consumptionPointInterval);

    consumptionPointInterval.editData = true;

    return this.forceUpdate();
  };

  _closeEditExistingInterval = (consumptionPointInterval: Aid.IAidConsumptionPointInterval, index = 0) => {
    if (this.state.consumptionPointsInterval) {
      this.state.consumptionPointsInterval[index] = consumptionPointInterval.oldData
        ? { ...consumptionPointInterval.oldData }
        : consumptionPointInterval;
    }

    return this.forceUpdate();
  };

  _downloadAnnex = (reportFormat: Utils.FileType) => async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      event.preventDefault();

      await this.setState({ loading: true });

      if (!this.state.annexStartDate) {
        throw new Error(
          ToString(Localization.formatString(Localization.backOfficeComputationAddFromError, ToString(this.state.minAnnexStartDate)))
        );
      } else if (!this.state.annexEndDate) {
        throw new Error(
          ToString(Localization.formatString(Localization.backOfficeComputationAddToError, ToString(this.state.maxAnnexEndDate)))
        );
      } else if (!ConsumptionPointValidation(this.props.match.params.consumptionPoint)) {
        throw new Error(ToString(Localization.backOfficeComputationAddConsumptionPointError));
      }

      const data = {
        consumptionPoint: this.props.match.params.consumptionPoint,
        endDate: this.state.annexEndDate,
        reportFormat,
        reportType: 'anexa11',
        startDate: this.state.annexStartDate
      } as Aid.IAidAssociationExport;

      const response = await AidsApi.ExportAssociationReport(data);

      const headerFileName = ToString(response.headers['content-disposition']);
      const localFileName = `${data.startDate} - ${data.endDate}`;

      await DownloadFile(response.data, `${headerFileName || localFileName}.${data.reportFormat}`);

      return this.setState({
        annexEndDate: '',
        annexStartDate: '',
        loading: false,
        maxAnnexEndDate: '',
        minAnnexStartDate: '',
        showModal: false
      });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _setValue = (event: React.ChangeEvent<HTMLInputElement>, consumptionPointInterval?: Aid.IAidConsumptionPointInterval) => {
    event.preventDefault();

    const element = event.currentTarget || event.target || {};

    if (consumptionPointInterval) {
      // @ts-ignore
      consumptionPointInterval[element.id as keyof Aid.IAidConsumptionPointInterval] = element.value;

      return this.forceUpdate(() => (element.id === 'startDate' || element.id === 'endDate') && this._getAids());
    }

    return this.setState(
      prevState => ({ ...prevState, [element.id]: element.value }),
      () => (element.id === 'startDate' || element.id === 'endDate') && this._getAids()
    );
  };

  _onMouseOver = () => this.setState({ stopPropagation: true });

  _onMouseOut = () => this.setState({ stopPropagation: false });

  _onClickCapture = (event: React.MouseEvent<HTMLDivElement>, consumptionPointInterval?: Aid.IAidConsumptionPointInterval) => {
    if (this.state.stopPropagation) {
      event.stopPropagation();

      return this.setState({
        annexEndDate: ToString(consumptionPointInterval && consumptionPointInterval.endDate),
        annexStartDate: ToString(consumptionPointInterval && consumptionPointInterval.startDate),
        maxAnnexEndDate: ToString(consumptionPointInterval && consumptionPointInterval.endDate),
        minAnnexStartDate: ToString(consumptionPointInterval && consumptionPointInterval.startDate),
        showModal: true
      });
    }

    return undefined;
  };

  _isNaturalGas = () => {
    return !!(
      this.state.partner &&
      this.state.partner.consumptionPoints &&
      this.state.partner.consumptionPoints.length &&
      this.state.partner.consumptionPoints[0] &&
      this.state.partner.consumptionPoints[0].sectorId === SECTOR_ID_GAS
    );
  };

  _setConsumption = (event: React.ChangeEvent<HTMLInputElement>, aids = this.state.aids) => {
    event.preventDefault();

    const element = event.currentTarget || event.target || {};

    const foundAid = aids && aids.find(aid => ToString(aid.personalIdentificationNumber) === ToString(element.name));

    if (!foundAid) {
      return undefined;
    }

    foundAid.consumption = element.value === '' ? '' : ToFloat(element.value);

    if (foundAid.consumption < 0) {
      foundAid.consumption = 0;
    }

    return this.forceUpdate(() => aids);
  };

  _sortConsumption = (consumptionPointsInterval = this.state.consumptionPointsInterval) => {
    if (consumptionPointsInterval && consumptionPointsInterval.length) {
      const newConsumptionPointsInterval = [...consumptionPointsInterval];

      return newConsumptionPointsInterval.sort((a, b) => b.consumptionPointIntervalId - a.consumptionPointIntervalId);
    }

    return undefined;
  };

  _getLastInterval = (consumptionPointsInterval = this.state.consumptionPointsInterval) => {
    const newConsumptionPointsInterval = this._sortConsumption(consumptionPointsInterval);

    if (newConsumptionPointsInterval && newConsumptionPointsInterval.length) {
      return newConsumptionPointsInterval.find(Boolean);
    }

    return undefined;
  };

  _getMinValue = (lastInterval = this._getLastInterval(), skipInterval = false) => {
    if (lastInterval && !skipInterval) {
      return DateStringToMinValue(lastInterval.endDate);
    }

    return GetFirstMonthAsDate(this.state.MONTHS);
  };

  _onCloseDateModal = () => {
    return this.setState({ dateWarning: false });
  };

  _onCloseModal = () => {
    return this.setState({ showModal: false });
  };

  _modalOkPress = () => {
    const index = this.state.index;

    if (index !== undefined) {
      this.setState({ index: undefined });

      return this._editInterval(this.state.consumptionPointsInterval && this.state.consumptionPointsInterval[index], index);
    }

    return this._createInterval();
  };
}

export default BackOfficeComputation;
